import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Synthesized sounds using Web Audio API ──────────────────────────────────
const playBhSound = (type) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    const sounds = {
      // 1: NO — heartbreak crack sound
      heartbreak: () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.3);
        gain.gain.setValueAtTime(0.4, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
        osc.start(); osc.stop(ctx.currentTime + 0.4);
      },
      // 2: DUE DI PICCHE — dark spooky
      spades: () => {
        [220, 185, 160].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.type = 'square';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.12);
          gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + i * 0.12 + 0.05);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.3);
          osc.start(ctx.currentTime + i * 0.12);
          osc.stop(ctx.currentTime + i * 0.12 + 0.3);
        });
      },
      // 3: CORNA — heavy metal riff
      rock: () => {
        [100, 150, 200].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const dist = ctx.createWaveShaper();
          const gain = ctx.createGain();
          const curve = new Float32Array(256);
          for (let j = 0; j < 256; j++) { const x = j * 2 / 256 - 1; curve[j] = x * 5; }
          dist.curve = curve;
          osc.connect(dist); dist.connect(gain); gain.connect(ctx.destination);
          osc.type = 'sawtooth';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.06);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.06 + 0.5);
          osc.start(ctx.currentTime + i * 0.06);
          osc.stop(ctx.currentTime + i * 0.06 + 0.5);
        });
      },
      // 4: HOT — sizzle/fire sound
      hot: () => {
        const bufferSize = ctx.sampleRate * 0.4;
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
        const source = ctx.createBufferSource();
        const filter = ctx.createBiquadFilter();
        const gain = ctx.createGain();
        filter.type = 'bandpass'; filter.frequency.value = 3000; filter.Q.value = 0.5;
        source.buffer = buffer;
        source.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        source.start(); source.stop(ctx.currentTime + 0.4);
      },
      // 5: LIKE — social media notification
      like: () => {
        [523, 659, 784].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.08);
          gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + i * 0.08 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.08 + 0.2);
          osc.start(ctx.currentTime + i * 0.08);
          osc.stop(ctx.currentTime + i * 0.08 + 0.2);
        });
      },
      // 6: SMILE — happy boing
      smile: () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(300, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.15);
        osc.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.3);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
        osc.start(); osc.stop(ctx.currentTime + 0.35);
      },
      // 7: CUORE DI GHIACCIO — ice crystal
      ice: () => {
        [1200, 1600, 2000, 1400].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.type = 'triangle';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.06);
          gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + i * 0.06 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.06 + 0.4);
          osc.start(ctx.currentTime + i * 0.06);
          osc.stop(ctx.currentTime + i * 0.06 + 0.4);
        });
      },
      // 8/9: KISS — kissing smack
      kiss: () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(800, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.4, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
        osc.start(); osc.stop(ctx.currentTime + 0.2);
        // Pop
        setTimeout(() => {
          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.connect(gain2); gain2.connect(ctx.destination);
          osc2.type = 'sine';
          osc2.frequency.value = 300;
          gain2.gain.setValueAtTime(0.3, ctx.currentTime);
          gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
          osc2.start(); osc2.stop(ctx.currentTime + 0.15);
        }, 120);
      },
      // 10: SEX — romantic swell
      sex: () => {
        [261, 329, 392, 523].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.06);
          gain.gain.linearRampToValueAtTime(0.2, ctx.currentTime + i * 0.06 + 0.1);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.06 + 0.6);
          osc.start(ctx.currentTime + i * 0.06);
          osc.stop(ctx.currentTime + i * 0.06 + 0.6);
        });
      },
    };
    sounds[type]?.();
    setTimeout(() => ctx.close(), 2000);
  } catch(e) { console.warn('BH sound error:', e); }
};

export const playBhCardSound = (cardValue) => {
  const map = { 1:'heartbreak', 2:'spades', 3:'rock', 4:'hot', 5:'like', 6:'smile', 7:'ice', 8:'kiss', 9:'kiss', 10:'sex' };
  playBhSound(map[cardValue]);
};

// ─── Visual effects ───────────────────────────────────────────────────────────

// 1: NO — broken heart shatter
const HeartbreakEffect = () => (
  <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
    className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.7,0.3,0.5,0] }}
      transition={{ duration:1.2, times:[0,0.1,0.4,0.6,1] }}
      style={{ background:'radial-gradient(ellipse at center,rgba(239,68,68,0.4),rgba(190,24,93,0.3) 60%,transparent 100%)' }}/>
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.8,0] }} transition={{ duration:1.2 }}
      style={{ boxShadow:'inset 0 0 120px 40px rgba(239,68,68,0.6)' }}/>
    {[...Array(14)].map((_,i) => (
      <motion.div key={i} className="absolute" style={{ left:'50%', top:'30%', fontSize:24+Math.random()*20 }}
        animate={{ x:(Math.random()-0.5)*400, y:Math.random()*400+100, opacity:[0,1,0], rotate:Math.random()*720, scale:[0.5,1.2,0] }}
        transition={{ duration:0.8+Math.random()*0.4, delay:Math.random()*0.3 }}>
        {['💔','❤️‍🩹','🩸'][Math.floor(Math.random()*3)]}
      </motion.div>
    ))}
  </motion.div>
);

// 2: DUE DI PICCHE — dark purple spades rain
const SpadesEffect = () => (
  <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
    className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.6,0.3,0.5,0] }} transition={{ duration:1.3 }}
      style={{ background:'radial-gradient(ellipse at center,rgba(109,40,217,0.35),rgba(88,28,135,0.25) 60%,transparent 100%)' }}/>
    {[...Array(12)].map((_,i) => (
      <motion.div key={i} style={{ position:'absolute', left:`${10+Math.random()*80}%`, top:-40, fontSize:20+Math.random()*24, opacity:0.9 }}
        animate={{ y:'110vh', rotate:Math.random()*360, opacity:[0,1,1,0] }}
        transition={{ duration:0.7+Math.random()*0.5, delay:Math.random()*0.4 }}>♠</motion.div>
    ))}
  </motion.div>
);

// 3: CORNA — rock explosion
const RockEffect = () => (
  <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
    className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.8,0.3,0.6,0] }} transition={{ duration:1.1 }}
      style={{ background:'radial-gradient(ellipse at center bottom,rgba(239,68,68,0.5),rgba(124,0,0,0.3) 60%,transparent 100%)' }}/>
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.7,0] }} transition={{ duration:1.1 }}
      style={{ boxShadow:'inset 0 0 120px 50px rgba(239,68,68,0.7)' }}/>
    {[...Array(10)].map((_,i) => (
      <motion.div key={i} style={{ position:'absolute', left:`${10+Math.random()*80}%`, top:'40%', fontSize:28 }}
        animate={{ x:(Math.random()-0.5)*300, y:(Math.random()-0.5)*300, opacity:[0,1,0], scale:[0.5,1.5,0] }}
        transition={{ duration:0.6+Math.random()*0.4, delay:Math.random()*0.2 }}>
        {['🤘','💥','🔥'][Math.floor(Math.random()*3)]}
      </motion.div>
    ))}
  </motion.div>
);

// 4: HOT — fire shimmer
const HotEffect = () => (
  <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
    className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.5,0.2,0.4,0] }} transition={{ duration:1.2 }}
      style={{ background:'radial-gradient(ellipse at center bottom,rgba(249,115,22,0.4),transparent 70%)' }}/>
    {[...Array(16)].map((_,i) => (
      <motion.div key={i} style={{ position:'absolute', left:`${5+i*6}%`, bottom:0, fontSize:16+Math.random()*16 }}
        animate={{ y:-(100+Math.random()*200), opacity:[0,1,0.8,0], scale:[0.5,1,0.3] }}
        transition={{ duration:0.5+Math.random()*0.5, delay:Math.random()*0.4 }}>🔥</motion.div>
    ))}
  </motion.div>
);

// 5: LIKE — hearts and thumbs up burst
const LikeEffect = () => (
  <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
    className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
    {[...Array(16)].map((_,i) => (
      <motion.div key={i} style={{ position:'absolute', left:`${10+Math.random()*80}%`, top:`${10+Math.random()*80}%`, fontSize:18+Math.random()*20 }}
        initial={{ scale:0, opacity:0 }}
        animate={{ scale:[0,1.5,1], opacity:[0,1,0], y:-60-Math.random()*80 }}
        transition={{ duration:0.7+Math.random()*0.4, delay:Math.random()*0.4 }}>
        {['👍','❤️','💕','✨'][Math.floor(Math.random()*4)]}
      </motion.div>
    ))}
  </motion.div>
);

// 6: SMILE — rainbow confetti
const SmileEffect = () => {
  const colors = ['#ec4899','#f97316','#eab308','#22c55e','#3b82f6','#a855f7'];
  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
      {[...Array(30)].map((_,i) => (
        <motion.div key={i} style={{
          position:'absolute', left:`${Math.random()*100}%`, top:-10,
          width:8+Math.random()*8, height:8+Math.random()*8, borderRadius:'50%',
          background:colors[Math.floor(Math.random()*colors.length)],
        }}
          animate={{ y:'110vh', x:(Math.random()-0.5)*200, rotate:Math.random()*720, opacity:[0,1,1,0] }}
          transition={{ duration:0.8+Math.random()*0.6, delay:Math.random()*0.4 }}/>
      ))}
      {[...Array(8)].map((_,i) => (
        <motion.div key={'e'+i} style={{ position:'absolute', left:`${10+Math.random()*80}%`, top:`${20+Math.random()*60}%`, fontSize:22 }}
          animate={{ scale:[0,1.5,1], opacity:[0,1,0] }}
          transition={{ duration:0.6, delay:i*0.07 }}>
          {['😊','😄','🥰','✨'][i%4]}
        </motion.div>
      ))}
    </motion.div>
  );
};

// 7: ICE HEART — freeze effect
const IceEffect = () => (
  <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
    className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.6,0.3,0.5,0] }} transition={{ duration:1.4 }}
      style={{ background:'radial-gradient(ellipse at center,rgba(56,189,248,0.25),rgba(14,165,233,0.15) 60%,transparent 100%)' }}/>
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.5,0] }} transition={{ duration:1.4 }}
      style={{ boxShadow:'inset 0 0 120px 40px rgba(56,189,248,0.4)' }}/>
    {[...Array(20)].map((_,i) => (
      <motion.div key={i} style={{ position:'absolute', left:`${Math.random()*100}%`, top:-20,
        fontSize:12+Math.random()*16, color:'rgba(186,230,253,0.9)' }}
        animate={{ y:'110vh', opacity:[0,1,1,0], rotate:Math.random()*360 }}
        transition={{ duration:0.8+Math.random()*0.5, delay:Math.random()*0.4 }}>❄️</motion.div>
    ))}
    {[...Array(6)].map((_,i) => (
      <motion.div key={'h'+i} style={{ position:'absolute', left:`${10+i*15}%`, top:`${20+Math.random()*60}%`, fontSize:20 }}
        animate={{ scale:[0,1.3,1], opacity:[0,1,0] }} transition={{ duration:0.5, delay:i*0.1 }}>🧊</motion.div>
    ))}
  </motion.div>
);

// 8/9: KISS — hearts and lips burst
const KissEffect = () => (
  <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
    className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.4,0.2,0.35,0] }} transition={{ duration:1.2 }}
      style={{ background:'radial-gradient(ellipse at center,rgba(244,114,182,0.3),transparent 70%)' }}/>
    {[...Array(14)].map((_,i) => (
      <motion.div key={i} style={{ position:'absolute', left:`${10+Math.random()*80}%`, top:`${10+Math.random()*80}%`, fontSize:18+Math.random()*18 }}
        initial={{ scale:0, opacity:0 }}
        animate={{ scale:[0,1.8,1], opacity:[0,1,0], y:-(40+Math.random()*100), x:(Math.random()-0.5)*100 }}
        transition={{ duration:0.7+Math.random()*0.4, delay:Math.random()*0.5 }}>
        {['💋','❤️','💕','💝','😘'][Math.floor(Math.random()*5)]}
      </motion.div>
    ))}
  </motion.div>
);

// 10: SEX — romantic hearts explosion
const SexEffect = () => (
  <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
    className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.5,0.25,0.45,0] }} transition={{ duration:1.5 }}
      style={{ background:'radial-gradient(ellipse at center,rgba(236,72,153,0.35),rgba(190,24,93,0.2) 60%,transparent 100%)' }}/>
    {[...Array(20)].map((_,i) => (
      <motion.div key={i} style={{ position:'absolute', left:`${5+Math.random()*90}%`, bottom:`${Math.random()*30}%`, fontSize:16+Math.random()*24 }}
        animate={{ y:-(100+Math.random()*300), x:(Math.random()-0.5)*150, opacity:[0,1,0.9,0], scale:[0.3,1.2,0.8] }}
        transition={{ duration:0.8+Math.random()*0.7, delay:Math.random()*0.5 }}>
        {['❤️','💕','💗','💓','💞','❤️‍🔥'][Math.floor(Math.random()*6)]}
      </motion.div>
    ))}
  </motion.div>
);

const EFFECT_MAP = {
  1: HeartbreakEffect,
  2: SpadesEffect,
  3: RockEffect,
  4: HotEffect,
  5: LikeEffect,
  6: SmileEffect,
  7: IceEffect,
  8: KissEffect,
  9: KissEffect,
  10: SexEffect,
};

const BhCardEffectOverlay = ({ cardValue }) => {
  const Effect = cardValue ? EFFECT_MAP[cardValue] : null;
  return (
    <AnimatePresence>
      {Effect && <Effect key={cardValue + '_' + Date.now()} />}
    </AnimatePresence>
  );
};

export default BhCardEffectOverlay;
